import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import PasswordGate from '../components/common/PasswordGate';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fixImageUrl, cn, formatDateSafe } from '../lib/utils';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { motion, AnimatePresence } from 'framer-motion';

const { FiArrowLeft, FiCalendar, FiMapPin, FiImage, FiX, FiChevronLeft, FiChevronRight } = FiIcons;

const Lightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const next = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4"
    >
      <button onClick={onClose} className="absolute top-8 right-8 text-white p-3 hover:bg-white/10 rounded-full transition-all">
        <SafeIcon icon={FiX} className="w-8 h-8" />
      </button>

      {images.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-8 text-white p-4 hover:bg-white/10 rounded-full transition-all">
            <SafeIcon icon={FiChevronLeft} className="w-10 h-10" />
          </button>
          <button onClick={next} className="absolute right-8 text-white p-4 hover:bg-white/10 rounded-full transition-all">
            <SafeIcon icon={FiChevronRight} className="w-10 h-10" />
          </button>
        </>
      )}

      <motion.div 
        key={currentIndex} 
        initial={{ scale: 0.9, opacity: 0 }} 
        animate={{ scale: 1, opacity: 1 }} 
        className="max-w-5xl max-h-[85vh] flex flex-col items-center"
      >
        <img src={fixImageUrl(images[currentIndex].url)} className="max-w-full max-h-[75vh] object-contain shadow-2xl rounded-lg" alt="" />
        {images[currentIndex].caption && (
          <p className="text-white mt-8 text-xl font-serif italic text-center max-w-2xl">{images[currentIndex].caption}</p>
        )}
        <p className="text-white/40 mt-4 text-sm font-bold tracking-widest uppercase">{currentIndex + 1} / {images.length}</p>
      </motion.div>
    </motion.div>
  );
};

const EntryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  useEffect(() => {
    fetchEntry();
  }, [id]);

  const fetchEntry = async () => {
    setLoading(true);
    const { data: entryData } = await supabase.from('entries_20240520').select('*').eq('id', id).single();
    if (entryData) {
      setEntry(entryData);
      const { data: mediaData } = await supabase.from('media_20240520').select('*').eq('entry_id', id).order('sort_order');
      setMedia(mediaData || []);
    }
    setLoading(false);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 animate-pulse bg-stone-50 rounded-[3rem] h-[80vh]" />;
  if (!entry) return <div className="text-center py-20 italic">Memory not found.</div>;

  const center = { lat: entry.lat, lng: entry.lng };

  return (
    <PasswordGate section={entry.section}>
      <article className="max-w-3xl mx-auto px-4 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center text-sm font-bold text-stone-400 hover:text-[#8c7851] mb-8 transition-colors">
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-4 h-4" />
          Back to Collection
        </button>

        <header className="mb-12">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#8c7851] mb-6">
            <span className="bg-[#8c7851]/10 px-3 py-1 rounded-full">{entry.section}</span>
            <span className="text-stone-300">•</span>
            <span>{formatDateSafe(entry.date)}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-8 leading-tight text-[#2c2c2c]">{entry.title}</h1>
          {entry.location_text && (
            <div className="flex items-center text-stone-600 font-medium bg-stone-50 w-fit px-5 py-2.5 rounded-full border border-stone-200">
              <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-[#8c7851]" />
              {entry.location_text}
            </div>
          )}
        </header>

        {entry.cover_image_url && (
          <button onClick={() => setLightboxIndex(0)} className="w-full rounded-[3rem] overflow-hidden mb-16 shadow-2xl ring-8 ring-white cursor-zoom-in">
            <img src={fixImageUrl(entry.cover_image_url)} alt="" className="w-full h-auto hover:scale-105 transition-transform duration-700" />
          </button>
        )}

        <div className="prose prose-stone max-w-none mb-20">
          <ReactMarkdown className="text-xl leading-relaxed text-[#2c2c2c] whitespace-pre-wrap font-serif first-letter:text-5xl first-letter:font-bold first-letter:mr-3 first-letter:float-left first-letter:text-[#8c7851]">
            {entry.body}
          </ReactMarkdown>
        </div>

        {entry.lat && entry.lng && (
          <section className="mb-20 bg-stone-50 p-8 rounded-[3.5rem] border border-stone-100">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiMapPin} className="text-[#8c7851]" />
              Recorded Location
            </h2>
            <div className="rounded-[2.5rem] overflow-hidden border-4 border-white shadow-xl h-[350px]">
              {isLoaded ? (
                <GoogleMap 
                  mapContainerStyle={{ width: '100%', height: '100%' }} 
                  center={center} 
                  zoom={14} 
                  options={{ disableDefaultUI: true, styles: mapStyles }}
                >
                  <Marker position={center} />
                </GoogleMap>
              ) : (
                <div className="h-full bg-stone-100 flex items-center justify-center">Loading Map...</div>
              )}
            </div>
          </section>
        )}

        {media.length > 0 && (
          <section className="pt-16 border-t border-stone-100">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-10 flex items-center gap-2">
              <SafeIcon icon={FiImage} className="text-[#8c7851]" />
              Moment Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {media.map((item, idx) => (
                <button 
                  key={item.id} 
                  onClick={() => setLightboxIndex(idx)} 
                  className={cn(
                    "group relative rounded-[2.5rem] overflow-hidden bg-stone-100 shadow-md hover:shadow-2xl transition-all duration-500 cursor-zoom-in",
                    idx % 3 === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-square"
                  )}
                >
                  <img src={fixImageUrl(item.url)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-lg font-serif italic text-left">{item.caption}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        <AnimatePresence>
          {lightboxIndex !== null && (
            <Lightbox 
              images={media.length > 0 ? media : [{ url: entry.cover_image_url }]} 
              initialIndex={lightboxIndex} 
              onClose={() => setLightboxIndex(null)} 
            />
          )}
        </AnimatePresence>
      </article>
    </PasswordGate>
  );
};

const mapStyles = [
  { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#e9e9e9" }, { "lightness": 17 }] },
  { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 20 }] },
  { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }, { "lightness": 17 }] },
  { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 21 }] },
];

export default EntryDetail;